import Image from "next/image";
import Link from "next/link";
import { StorefrontIcon } from "@/components/storefront-icon";
import { requestCookieConsentPrompt } from "@/lib/cookie-consent";
import type { StorefrontShellData } from "@/lib/storefront-shell-types";
import type { StorefrontSiteConfig } from "@/lib/storefront-site";

type StorefrontFooterProps = {
  shellData?: StorefrontShellData;
  site: StorefrontSiteConfig;
};

function contactLines(address: string) {
  return address
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function toExternalUrl(url: string) {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

export function StorefrontFooter({ shellData, site }: StorefrontFooterProps) {
  const footer = shellData?.footer;
  const categories = shellData?.menuCategories ?? [];

  if (!footer) {
    return null;
  }

  return (
    <footer className="storefront-footer-background border-t border-white/10 text-white">
      <div className="mx-auto max-w-6xl px-4 pb-8 pt-5 sm:px-6 sm:pb-10 sm:pt-6">
        <div className="grid gap-5 lg:grid-cols-3 lg:gap-6">
          <section className="rounded-[2rem] border border-white/18 bg-black/12 p-5 text-center backdrop-blur-sm">
            <Link href="/" aria-label="Go to homepage" className="inline-flex">
              <Image
                src={site.desktopLogo}
                alt={site.brandName}
                width={188}
                height={42}
                className="h-auto w-[11.5rem]"
              />
            </Link>
            <div className="mt-6 space-y-4 text-sm leading-6 text-white/84">
              {footer.contact.storeName ? (
                <p className="text-base font-semibold text-white">
                  {footer.contact.storeName}
                </p>
              ) : null}
              {contactLines(footer.contact.storeAddress).length > 0 ? (
                <div>
                  {contactLines(footer.contact.storeAddress).map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                </div>
              ) : null}
              {footer.contact.telephoneNumber ? (
                <p>
                  <a
                    href={`tel:${footer.contact.telephoneNumber.replace(/\s+/g, "")}`}
                    className="transition-colors hover:text-white"
                  >
                    {footer.contact.telephoneNumber}
                  </a>
                </p>
              ) : null}
            </div>
          </section>

          <section className="rounded-[2rem] border border-white/18 bg-black/12 p-5 text-center backdrop-blur-sm">
            <p className="text-[0.72rem] font-bold uppercase tracking-[0.28em] text-white">
              Our Menu
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2.5">
              {categories.map((category) => (
                <Link
                  key={category.slug}
                  href={`/menu/${category.slug}`}
                  className="inline-flex items-center justify-center rounded-full border border-white/12 bg-black/10 px-4 py-2.5 text-sm font-medium text-white/84 transition-colors hover:bg-white hover-storefront-text-primary"
                >
                  {category.name}
                </Link>
              ))}
            </div>
          </section>

          <section className="rounded-[2rem] border border-white/18 bg-black/12 p-5 text-center backdrop-blur-sm">
            <p className="text-[0.72rem] font-bold uppercase tracking-[0.28em] text-white">
              Get Social
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              {footer.socialLinks.map((link) => (
                <a
                  key={`${link.platform}-${link.url}`}
                  href={toExternalUrl(link.url)}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={link.platform}
                  className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-white/12 bg-black/10 text-white transition-colors hover:bg-white hover-storefront-text-primary"
                >
                  <StorefrontIcon
                    asset={link.icon}
                    context="social"
                    label={link.platform}
                    className="h-5 w-5"
                  />
                </a>
              ))}
            </div>
          </section>
        </div>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-white/70">
          <Link
            href="/more/privacy-policy"
            className="underline underline-offset-2 transition-colors hover:text-white"
          >
            Privacy Policy
          </Link>
          <button
            type="button"
            onClick={requestCookieConsentPrompt}
            className="underline underline-offset-2 transition-colors hover:text-white"
          >
            Cookie preferences
          </button>
        </div>
      </div>
    </footer>
  );
}
